import React, { useEffect, useState } from "react";
import { Grid, Button, MenuItem, Menu } from "@mui/material";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Filler, Tooltip, Legend } from "chart.js";
import { exportRevenueReportAction, getRevenueReportAction } from "../../Actions/ReportsAction";
import { MyExportIcon } from "../../Assets/svg/MyIcons";
import { TableFilte } from "../../Assets/svg/svgicon";
import CustomSnackbar from "../Common/snackBar";
import FilterDrawer from "../../components/DataTable/FilterDrawer";
import {
    areaSeries,
    barChartOptions,
    barSeries,
    ChartLegend,
    ChartPanel,
    legendFromChartData,
    lineChartOptions,
    portalColor,
    ringCenterPlugin,
    ringChartOptions,
    ringSeries,
    statusColor,
} from "./reportCharts";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Filler, Tooltip, Legend);

const drawerFilters = [
    {
        name: "portal",
        label: "Portal",
        type: "portal",
        defaultValue: "all",
        options: [
            { label: "All Portal", value: "all" },
            { label: "QBot", value: "qbot" },
            { label: "PayCentral", value: "paycentral" },
            { label: "KYC", value: "kyc" },
            { label: "Tripola", value: "tripola" },
        ],
    },
    {
        name: "status",
        label: "Status",
        type: "status",
        defaultValue: "all",
        options: [
            { label: "All Status", value: "all" },
            { label: "Success", value: "1" },
            { label: "Failed", value: "2" },
            { label: "Refunded", value: "3" },
        ],
    },
    {
        name: "dateRange",
        label: "Date Range",
        type: "dateRange",
        placeholder: "Select date range",
    },
];

const RevenueReport = () => {
    const [loading, setLoading] = useState(false);

    const getDefaultDateRange = () => {
        const to = new Date();
        const from = new Date();
        from.setMonth(from.getMonth() - 6);
        return { from, to };
    };

    const [filterValues, setFilterValues] = useState({
        portal: "all",
        status: "all",
        dateRange: getDefaultDateRange(),
    });

    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const [summary, setSummary] = useState({
        totalRevenue: 0,
        totalTransactions: 0,
        successfulTransactions: 0,
        failedTransactions: 0,
    });
    const [transactions, setTransactions] = useState([]);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [exportLoader, setExportLoader] = useState(false);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);

    const isExportMenuOpen = Boolean(exportAnchorEl);

    const buildApiFilters = (fv) => ({
        portal: fv.portal === "all" ? "" : fv.portal,
        status: fv.status === "all" ? "" : fv.status,
        startDate: fv.dateRange?.from ? fv.dateRange.from.toISOString().split("T")[0] : "",
        endDate: fv.dateRange?.to ? fv.dateRange.to.toISOString().split("T")[0] : "",
    });

    const fetchRevenueReport = async (fv = filterValues) => {
        try {
            setLoading(true);
            const response = await getRevenueReportAction(buildApiFilters(fv));
            if (response?.status === 200) {
                setSummary(response?.data?.summary || {});
                setTransactions(response?.data?.transactions || []);
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || "Something went wrong",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRevenueReport();
    }, []);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFilterApply = (newValues) => {
        setFilterValues(newValues);
        fetchRevenueReport(newValues);
    };

    const handleFilterReset = () => {
        const resetValues = { portal: "all", status: "all", dateRange: getDefaultDateRange() };
        setFilterValues(resetValues);
        fetchRevenueReport(resetValues);
    };

    const hasAppliedFilters =
        filterValues.portal !== "all" ||
        filterValues.status !== "all";

    const revenueTrendData = (() => {
        const monthlyRevenue = {};
        transactions.forEach((item) => {
            const date = new Date(item.transactionDate);
            const month = date.toLocaleString("default", { month: "short", year: "numeric" });
            if (!monthlyRevenue[month]) monthlyRevenue[month] = 0;
            monthlyRevenue[month] += Number(item.amount || 0);
        });
        return {
            labels: Object.keys(monthlyRevenue),
            datasets: [
                areaSeries({
                    label: "Monthly Revenue",
                    data: Object.values(monthlyRevenue),
                }),
            ],
        };
    })();

    const portalRevenueData = (() => {
        const portals = ["qbot", "paycentral", "kyc", "tripola"];
        return {
            labels: portals.map((item) => item.toUpperCase()),
            datasets: [
                barSeries({
                    label: "Revenue",
                    data: portals.map((portal) =>
                        transactions
                            .filter((item) => item.portal?.toLowerCase() === portal)
                            .reduce((sum, item) => sum + Number(item.amount || 0), 0)
                    ),
                    colors: portals.map((portal) => portalColor(portal)),
                }),
            ],
        };
    })();

    const statusChartData = {
        labels: ["Success", "Failed", "Refunded", "Pending"],
        datasets: [
            ringSeries({
                data: [
                    transactions.filter((item) => item.status === "1").length,
                    transactions.filter((item) => item.status === "2").length,
                    transactions.filter((item) => item.status === "3").length,
                    transactions.filter((item) => !["1", "2", "3"].includes(item.status)).length,
                ],
                colors: [
                    statusColor("success"),
                    statusColor("failed"),
                    statusColor("refunded"),
                    statusColor("pending"),
                ],
            }),
        ],
    };

    const hasTransactions = transactions.length > 0;

    const handleExportConfirm = async (type) => {
        try {
            setExportLoader(true);
            const payload = {
                exportType: type,
                ...buildApiFilters(filterValues),
            };
            const res = await exportRevenueReportAction(payload);
            if (res.type === "application/json") return;

            const blob = new Blob([res]);
            const timestamp = new Date().getTime();
            let fileName = "";
            switch (type) {
                case "Excel": fileName = `revenue_report_${timestamp}.xlsx`; break;
                case "PDF": fileName = `revenue_report_${timestamp}.pdf`; break;
                default: fileName = `revenue_report_${timestamp}`;
            }
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            URL.revokeObjectURL(link.href);
            document.body.removeChild(link);
        } catch (error) {
            console.log(error);
        } finally {
            setExportLoader(false);
        }
    };

    const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
    const handleExportMenuClose = () => setExportAnchorEl(null);
    const handleExportOption = (type) => {
        handleExportConfirm(type);
        handleExportMenuClose();
    };

    return (
        <>
            <Grid container spacing={3}>
                {/* HEADER + GLOBAL FILTER */}
                <Grid item xs={12}>
                    <div className="card border-rounded-0 fiter-card">
                        <div className="card-body p-5 pb-0">
                            <div className="row align-items-center mb-5">
                                {/* Title */}
                                <div className="col-auto">
                                    <h2 className="chart-title mb-0">Revenue Report</h2>
                                </div>

                                {/* Buttons */}
                                <div className="col-auto ms-auto d-flex gap-2">
                                    {/* Export Button */}
                                    {/* <Button
                                        className="btn btn-primary"
                                        onClick={handleExportClick}
                                        disabled={exportLoader}
                                    >
                                        <MyExportIcon className="text-white me-1" />
                                        Export
                                        {exportLoader && (
                                            <span
                                                className="spinner-border spinner-border-sm ms-2"
                                                role="status"
                                            />
                                        )}
                                    </Button> */}

                                    <Menu
                                        anchorEl={exportAnchorEl}
                                        open={isExportMenuOpen}
                                        onClose={handleExportMenuClose}
                                    >
                                        <MenuItem onClick={() => handleExportOption("Excel")}>
                                            Export as Excel
                                        </MenuItem>
                                        <MenuItem onClick={() => handleExportOption("PDF")}>
                                            Export as PDF
                                        </MenuItem>
                                    </Menu>

                                    {/* Filter Button */}
                                    <Button
                                        variant="outlined"
                                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                        startIcon={<TableFilte />}
                                        className={`dt-filter-btn ${hasAppliedFilters ? "active" : ""}`}
                                    >
                                        Filters
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Grid>
            </Grid>

            {/* CHARTS */}
            <Grid container spacing={3} mt={1}>
                {/* Revenue Trend */}
                <Grid item xs={12} md={8}>
                    <ChartPanel
                        title="Revenue trend"
                        caption="Revenue booked per month"
                        height={330}
                        loading={loading}
                        isEmpty={!hasTransactions}
                    >
                        <Line
                            key={transactions.length + "-line"}
                            data={revenueTrendData}
                            options={lineChartOptions()}
                        />
                    </ChartPanel>
                </Grid>

                {/* Status Doughnut */}
                <Grid item xs={12} md={4}>
                    <ChartPanel
                        title="Revenue status"
                        caption="Share of transactions by outcome"
                        height={330}
                        loading={loading}
                        isEmpty={!hasTransactions}
                    >
                        <div className="report-ring">
                            <div className="report-ring-chart">
                                <Doughnut
                                    key={transactions.length + "-doughnut"}
                                    data={statusChartData}
                                    options={{
                                        ...ringChartOptions(),
                                        plugins: {
                                            ...ringChartOptions().plugins,
                                            reportRingCenter: { caption: "Transactions" },
                                        },
                                    }}
                                    plugins={[ringCenterPlugin]}
                                />
                            </div>
                            <ChartLegend items={legendFromChartData(statusChartData)} />
                        </div>
                    </ChartPanel>
                </Grid>

                {/* Portal Revenue */}
                <Grid item xs={12}>
                    <ChartPanel
                        title="Revenue by portal"
                        caption="Total revenue collected per product"
                        height={320}
                        loading={loading}
                        isEmpty={!hasTransactions}
                    >
                        <Bar
                            key={transactions.length + "-bar"}
                            data={portalRevenueData}
                            options={barChartOptions()}
                        />
                    </ChartPanel>
                </Grid>
            </Grid>

            {/* Filter Drawer */}
            <FilterDrawer
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                filters={drawerFilters}
                filterValues={filterValues}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
            />

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default RevenueReport;